import type { Fish } from '../components/april';
import type { Locale } from '../i18n/strings';

export const SKINS = [
  'aqua',
  'y2k',
  'saas',
  'paint',
  'pixel',
  'matrix',
  'xp',
  'skyblog',
  'threed',
  'wiki',
  'search',
] as const;
export type Skin = (typeof SKINS)[number];

export function isSkin(value: unknown): value is Skin {
  return typeof value === 'string' && (SKINS as readonly string[]).includes(value);
}

/**
 * Les quatre thèmes du skin skyblog.
 *
 * Le visiteur les choisit dans la boîte « CUSTOMISE MON SKYBLOG ». Le choix est
 * mémorisé et **survit au skin** : s'il revient sur une autre interface puis
 * retombe sur le skyblog, son thème l'attend.
 */
export const SB_THEMES = ['pink', 'neon', 'emo', 'steve'] as const;
export type SkyblogTheme = (typeof SB_THEMES)[number];

export function isSkyblogTheme(value: unknown): value is SkyblogTheme {
  return typeof value === 'string' && (SB_THEMES as readonly string[]).includes(value);
}

/** Réglages du mixeur (cf. handoff § Mixeur & pad). */
export interface MixState {
  readonly rate: number;
  readonly cutoff: number;
  readonly echo: number;
  readonly vol: number;
  readonly wobble: boolean;
}

/** Bornes des quatre curseurs, partagées par les trois skins et par l'audio. */
export const MIX_RANGES = {
  rate: { min: 0.5, max: 2, step: 0.05 },
  cutoff: { min: 220, max: 18000, step: 20 },
  echo: { min: 0, max: 0.8, step: 0.02 },
  vol: { min: 0, max: 1, step: 0.02 },
} as const;

export const DEFAULT_MIX: MixState = {
  rate: 1,
  cutoff: 18000,
  echo: 0,
  // Départ à mi-course : le morceau ne doit jamais surprendre au premier clic,
  // le curseur de niveau est là pour monter.
  vol: 0.5,
  wobble: false,
};

export const PRESETS = {
  normal: { rate: 1, cutoff: 18000, echo: 0, wobble: false },
  deep: { rate: 0.85, cutoff: 520, echo: 0.28, wobble: true },
  fast: { rate: 1.55, cutoff: 18000, echo: 0.08, wobble: false },
  choir: { rate: 1, cutoff: 3800, echo: 0.66, wobble: true },
} as const satisfies Record<string, Partial<MixState>>;

export type PresetName = keyof typeof PRESETS;

/**
 * Repères du morceau, relevés dans Ableton et notés comme le logiciel les
 * affiche : **mesure.temps.double-croche**, à partir de 1.
 *
 * `steve.mp3` est à 120 BPM (une mesure = 2 s) et la mesure 1 tombe à 0,25 s
 * dans le fichier exporté, qui commence par un silence de 0,29 s. Ces deux
 * constantes suffisent à convertir ; pour changer les découpes, il n'y a que
 * cette table à remplacer, telle qu'elle se lit à l'écran d'Ableton.
 */
export const SONG_BPM = 120;
export const SONG_BAR_ONE_S = 0.25;

/**
 * Un repère du morceau : où l'extrait commence, et combien de temps il dure.
 *
 * `beats` est facultatif — sans lui, l'extrait fait `PAD_SLICE_BEATS`. C'est là
 * qu'on affine au cas par cas, à l'oreille : « il est orange » a besoin de deux
 * temps de plus pour poser son mot, « palala palala » de trois quarts de temps
 * en moins pour ne pas traîner, et la longue série de « la-la-la » ne veut rien
 * dire si on la coupe au bout d'une seconde et demie.
 *
 * Rallonger est sans risque tant que l'extrait s'arrête avant le repère
 * suivant : un test balaie la table et le vérifie.
 */
interface PadMark {
  readonly at: readonly [bar: number, beat: number, sixteenth: number];
  readonly beats?: number;
}

const PAD_MARKS: readonly PadMark[] = [
  { at: [1, 2, 1] }, // intro
  { at: [3, 2, 3] }, // le poisson steve
  { at: [4, 3, 3] }, // poisson steeeve
  { at: [5, 3, 1], beats: 5 }, // il est orange — rallongé, le repère suivant est à 8 temps
  { at: [7, 3, 1] }, // il a des bras
  { at: [8, 3, 3] }, // et des jambes
  { at: [10, 1, 1] }, // whuuum
  { at: [11, 3, 1] }, // da-da-da-da
  { at: [12, 3, 3], beats: 2.25 }, // palala palala — raccourci d'un quart de temps
  { at: [15, 1, 1], beats: 8 }, // la-la-la ×7 — la série entière dure 5,1 s
  { at: [21, 2, 2] }, // il est vraiment très beau
  { at: [23, 1, 1], beats: 6 }, // il peut nager sur la terre
  { at: [25, 2, 1] }, // il peut marcher dans l'eau
  { at: [28, 1, 4], beats: 6 }, // il sent forcément mauvais — c'est une chute
  { at: [30, 3, 2] }, // mais on l'aime bien
];

/** Instant de départ de chaque pad, en secondes dans le fichier. */
export const PAD_TIMES: readonly number[] = PAD_MARKS.map(
  ({ at: [bar, beat, sixteenth] }) =>
    SONG_BAR_ONE_S +
    ((bar - 1) * 4 + (beat - 1) + (sixteenth - 1) / 4) * (60 / SONG_BPM),
);

export const PAD_COUNT = PAD_TIMES.length;

/**
 * Durée par défaut d'un extrait, et période de re-déclenchement en bégaiement.
 *
 * Exprimée en temps plutôt qu'en millisecondes rondes : sur cette grille, un
 * extrait tombe toujours au même endroit de la mesure suivante. Trois temps à
 * 120 BPM font 1,5 s — de quoi laisser finir le mot commencé.
 *
 * Les pads qui ont besoin d'autre chose portent leur propre `beats` ci-dessus ;
 * c'est le seul endroit à toucher pour réaccorder une découpe.
 */
export const PAD_SLICE_BEATS = 3;
export const PAD_SLICE_MS = (PAD_SLICE_BEATS * 60_000) / SONG_BPM;

/** Durée de chaque extrait, dans l'ordre des pads. */
export const PAD_SLICES_MS: readonly number[] = PAD_MARKS.map(
  ({ beats = PAD_SLICE_BEATS }) => (beats * 60_000) / SONG_BPM,
);

/**
 * Les lignes de journal sont stockées sous forme de références, pas de texte :
 * changer de langue re-localise le journal déjà écrit au lieu de le figer dans
 * la langue d'origine.
 */
export type SimpleLogKey =
  | 'song_start'
  | 'song_stop'
  | 'pad_stop'
  | 'hold_on'
  | 'hold_off'
  | 'wobble_on'
  | 'wobble_off'
  | 'preset_normal'
  | 'preset_deep'
  | 'preset_fast'
  | 'preset_choir'
  | 'guestbook'
  | 'sb_theme'
  | 'kikoo'
  | 'kikoo_off';

export type VariantLogKey = 'boot' | 'feed' | 'praise' | 'pad';

/** Contenu d'une ligne, sans son identifiant. */
export type LogEntryData =
  | { readonly kind: 'simple'; readonly key: SimpleLogKey }
  | { readonly kind: 'variant'; readonly key: VariantLogKey; readonly variant: number }
  | { readonly kind: 'bubbles'; readonly count: number };

export type LogEntry = LogEntryData & { readonly id: number };

/** Le journal ne garde que les 7 dernières lignes (cf. handoff). */
export const LOG_LIMIT = 7;

/**
 * Une entrée du livre d'or est soit l'une des trois entrées de départ (stockée
 * par indice pour rester traduisible), soit un message du visiteur.
 */
export type GuestbookEntry =
  | { readonly id: number; readonly kind: 'seed'; readonly index: number }
  | { readonly id: number; readonly kind: 'visitor'; readonly text: string };

export interface SteveState {
  readonly skin: Skin;
  readonly lang: Locale;
  /** Thème du skin skyblog : mémorisé, et indépendant du skin tiré. */
  readonly sbTheme: SkyblogTheme;
  /**
   * Pseudo adopté au générateur du skyblog, ou chaîne vide.
   *
   * C'est la signature du visiteur **sur les onze interfaces** : dès qu'il en a
   * un, c'est lui qui apparaît au-dessus de ses messages, à la place de
   * `visiteur_anonyme`. Mémorisé, comme le thème.
   */
  readonly kikoo: string;
  /**
   * Les poissons en papier collés sur l'interface, du mot secret `poisson`.
   *
   * Mémorisés eux aussi, et **ils survivent au tirage du skin** : on ne les
   * laisse pas sur une interface, on les laisse sur le site.
   */
  readonly fish: readonly Fish[];
  readonly seen: number;
  readonly meals: number;
  readonly playing: boolean;
  readonly audioMissing: boolean;
  readonly mix: MixState;
  readonly hold: boolean;
  /** Steve-curseur : coupable depuis le menu Steve du skin paint. */
  readonly followCursor: boolean;
  readonly log: readonly LogEntry[];
  readonly guestbook: readonly GuestbookEntry[];
  readonly draft: string;
  readonly feedFx: number;
  readonly praiseFx: number;
  readonly bubbleFx: number;
  readonly egg: boolean;
  readonly clock: string;
  /** Horodatage de la dernière publication, pour espacer les envois. */
  readonly lastPostAt: number;
  /** Compteur d'identifiants, pour donner une clé stable à chaque ligne. */
  readonly nextId: number;
}

/**
 * Compteur « poissons vus » : borné, sinon une clé `localStorage` trafiquée
 * remonterait telle quelle jusqu'à l'affichage (et le skin y2k la pad à
 * 6 chiffres).
 */
export const MAX_SEEN = 999_999;

/** Intervalle minimum entre deux publications, en millisecondes. */
export const POST_COOLDOWN_MS = 700;
